import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Sidebar } from '@/components/Sidebar';
import { ImageViewer } from '@/components/ImageViewer';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowLeft, Send, Mic, MicOff, User, MapPin, FileText,
  MessageSquare, Clock, StickyNote, ShoppingBag, Save, Truck, Search, Copy, ExternalLink, Trash2,
  CreditCard, CheckCircle, XCircle, DollarSign, AlertTriangle, Phone, PhoneCall, PhoneMissed,
  Image, Volume2, FileVideo, File as FileIcon, Loader2, Paperclip
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const statusLabels = {
  sem_contato: 'Sem Contato',
  agendamento: 'Agendamento',
  venda: 'Venda',
  audio_enviado: 'Audio Enviado',
  nao_interesse: 'Nao Interesse',
  trote: 'Trote',
  reprovado: 'Reprovado',
};

const safeFormat = (dateStr, fmt, opts) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return format(d, fmt, opts);
  } catch { return ''; }
};

// URL direta pra streaming de mídia (browser cacheia + streaming nativo — igual WhatsApp Web)
const mediaStreamUrl = (msgId) => {
  const token = localStorage.getItem('token') || '';
  return `${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/media-stream/${msgId}?token=${encodeURIComponent(token)}`;
};

function ImageThumbnail({ dataUri, msg, onError }) {
  const [showViewer, setShowViewer] = React.useState(false);
  return (
    <div>
      <img
        src={dataUri}
        alt="Imagem"
        className="max-w-full rounded-md max-h-64 cursor-pointer hover:opacity-90 transition-opacity"
        onClick={() => setShowViewer(true)}
        onError={onError}
        data-testid={`media-image-${msg.id}`}
      />
      {msg.message && msg.message !== '[Imagem recebida]' && <p className="text-sm mt-1">{msg.message}</p>}
      {showViewer && (
        <ImageViewer src={dataUri} alt="Imagem" onClose={() => setShowViewer(false)} />
      )}
    </div>
  );
}

function MediaMessage({ msg }) {
  const isMedia = msg.is_image || msg.is_audio || msg.is_video || msg.is_document;

  // FAST PATH: se a mensagem já vem com media_base64 no payload (novo /messages endpoint),
  // renderiza direto via data:URI. Zero fetches adicionais. Sem depender de endpoints /media ou /media-stream.
  const inlineB64 = msg.media_base64;
  const inlineMime = ((msg.media_mimetype || '').split(';')[0] || (
    msg.is_audio ? 'audio/ogg' :
    msg.is_image ? 'image/jpeg' :
    msg.is_video ? 'video/mp4' :
    'application/octet-stream'
  ));

  const [errored, setErrored] = React.useState(false);
  const [loading, setLoading] = React.useState((msg.is_audio || msg.is_video) && !inlineB64);
  const [reloadKey, setReloadKey] = React.useState(0);
  const [fallbackUri, setFallbackUri] = React.useState(null);
  const [triedFallback, setTriedFallback] = React.useState(false);

  const tryFallback = React.useCallback(async () => {
    if (triedFallback) return false;
    setTriedFallback(true);
    try {
      const res = await api.getMedia(msg.id);
      if (res.data?.base64) {
        const mime = (res.data.mimetype || '').split(';')[0] ||
          (msg.is_audio ? 'audio/ogg' : msg.is_image ? 'image/jpeg' : msg.is_video ? 'video/mp4' : 'application/octet-stream');
        setFallbackUri(`data:${mime};base64,${res.data.base64}`);
        setLoading(msg.is_audio || msg.is_video);
        return true;
      }
    } catch { /* silencioso */ }
    return false;
  }, [msg.id, msg.is_audio, msg.is_image, msg.is_video, triedFallback]);

  if (!isMedia) {
    return <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>;
  }

  // Prioridade: (1) inline b64 do payload /messages, (2) fallback base64 buscado, (3) streaming
  let src;
  if (inlineB64) {
    src = `data:${inlineMime};base64,${inlineB64}`;
  } else if (fallbackUri) {
    src = fallbackUri;
  } else {
    src = reloadKey > 0
      ? `${mediaStreamUrl(msg.id)}&r=${reloadKey}`
      : mediaStreamUrl(msg.id);
  }

  const handleError = async () => {
    // Se já estava usando inline b64 e mesmo assim erro, tenta fallback via /media
    const recovered = await tryFallback();
    if (!recovered) {
      setLoading(false);
      setErrored(true);
    }
  };
  const handleReady = () => setLoading(false);
  const manualReload = () => {
    setErrored(false);
    setFallbackUri(null);
    setTriedFallback(false);
    setLoading(msg.is_audio || msg.is_video);
    setReloadKey(k => k + 1);
  };

  if (errored) {
    return (
      <div className="flex items-center gap-2 py-1 flex-wrap">
        {msg.is_image && <Image className="w-4 h-4 opacity-60" />}
        {msg.is_audio && <Volume2 className="w-4 h-4 opacity-60" />}
        {msg.is_video && <FileVideo className="w-4 h-4 opacity-60" />}
        {msg.is_document && <FileIcon className="w-4 h-4 opacity-60" />}
        <p className="text-sm opacity-70">{msg.message}</p>
        <span className="text-xs opacity-50">Ainda nao disponivel</span>
        <button
          type="button"
          onClick={manualReload}
          className="text-xs px-2 py-0.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid={`media-reload-${msg.id}`}
        >
          Recarregar
        </button>
      </div>
    );
  }

  if (msg.is_image) {
    return <ImageThumbnail dataUri={src} msg={msg} onError={handleError} />;
  }

  if (msg.is_audio) {
    return (
      <div data-testid={`media-audio-${msg.id}`} className="relative">
        {loading && (
          <div className="flex items-center gap-2 py-1 text-xs opacity-60">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>Carregando audio...</span>
          </div>
        )}
        <audio
          key={src}
          controls
          className={`max-w-full ${loading ? 'opacity-40' : ''}`}
          preload="auto"
          src={src}
          onError={handleError}
          onLoadedMetadata={handleReady}
          onCanPlay={handleReady}
        >
          Seu navegador nao suporta audio.
        </audio>
      </div>
    );
  }

  if (msg.is_video) {
    return (
      <div data-testid={`media-video-${msg.id}`}>
        {loading && (
          <div className="flex items-center gap-2 py-1 text-xs opacity-60">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>Carregando video...</span>
          </div>
        )}
        <video
          key={src}
          controls
          className={`max-w-full rounded-md max-h-64 ${loading ? 'opacity-40' : ''}`}
          preload="metadata"
          src={src}
          onError={handleError}
          onLoadedMetadata={handleReady}
          onCanPlay={handleReady}
        />
        {msg.message && msg.message !== '[Video recebido]' && <p className="text-sm mt-1">{msg.message}</p>}
      </div>
    );
  }

  // Documento — link de download direto
  return (
    <div className="flex items-center gap-2 py-1">
      <FileIcon className="w-4 h-4 opacity-60" />
      <a
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm underline hover:opacity-80"
        data-testid={`media-doc-${msg.id}`}
      >
        {msg.message || 'Baixar documento'}
      </a>
    </div>
  );
}

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isPrivileged = user?.role === 'admin' || user?.role === 'supervisor';
  const messagesEndRef = useRef(null);

  const [lead, setLead] = useState(null);
  const [messages, setMessages] = useState([]);
  const [notes, setNotes] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [products, setProducts] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [newNote, setNewNote] = useState('');
  const [noteTarget, setNoteTarget] = useState('team');
  const [allOperators, setAllOperators] = useState([]);
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const recordingRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const audioMimeTypeRef = useRef('');
  const [details, setDetails] = useState({
    name: '', email: '', cpf: '', birth_date: '', phone2: '', cep: '', street: '',
    number: '', complement: '', neighborhood: '', city: '', state: '', observation: ''
  });
  const [saleForm, setSaleForm] = useState({ product_id: '', observation: '', payment_method: '', card_holder: '', card_number: '', card_expiry: '', card_cvv: '', installments: '1', custom_amount: '' });
  const [cardError, setCardError] = useState('');
  const [trackings, setTrackings] = useState([]);
  const [trackingCode, setTrackingCode] = useState('');
  const [trackingDesc, setTrackingDesc] = useState('');
  const [trackingStatus, setTrackingStatus] = useState(null);
  const [loadingTracking, setLoadingTracking] = useState(false);
  const [installments, setInstallments] = useState([]);
  const [creditAnalyses, setCreditAnalyses] = useState([]);
  const [installmentForm, setInstallmentForm] = useState({ total_amount: '', num_installments: '1', first_due_date: '', description: '' });
  const [creditNotes, setCreditNotes] = useState('');

  const loadData = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        api.getLead(id),
        api.getMessages(id),
        api.getNotes(id),
        api.getTimeline(id),
        api.getProducts(),
        api.getTrackings(id),
        api.getInstallments(id),
        api.getCreditAnalysis(id),
        api.getOperators()
      ]);
      const val = (i) => results[i].status === 'fulfilled' ? results[i].value : null;
      const leadRes = val(0);
      if (!leadRes) { toast.error('Erro ao carregar lead'); return; }
      setLead(leadRes.data);
      setMessages(val(1)?.data || []);
      setNotes(val(2)?.data || []);
      setTimeline(val(3)?.data || []);
      setProducts((val(4)?.data || []).filter(p => p.active !== false));
      setTrackings(val(5)?.data || []);
      setInstallments(val(6)?.data || []);
      setCreditAnalyses(val(7)?.data || []);
      setAllOperators(val(8)?.data || []);
      const l = leadRes.data;
      setDetails({
        name: l.name || '', email: l.email || '',
        cpf: l.cpf || '', birth_date: l.birth_date || '', phone2: l.phone2 || '',
        cep: l.cep || '', street: l.street || '', number: l.number || '',
        complement: l.complement || '', neighborhood: l.neighborhood || '',
        city: l.city || '', state: l.state || '', observation: l.observation || ''
      });
    } catch { toast.error('Erro ao carregar lead'); }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Mark messages as read when opening lead detail
  useEffect(() => {
    if (id) {
      api.markRead(id).catch(() => {});
    }
  }, [id]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !lead) return;
    setSending(true);
    try {
      await api.sendWhatsAppText(lead.phone, newMessage, lead.id);
      setNewMessage('');
      const msgRes = await api.getMessages(id);
      setMessages(msgRes.data);
    } catch { toast.error('Erro ao enviar mensagem'); }
    finally { setSending(false); }
  };

  const handleSendNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    try {
      await api.createNote({ lead_id: id, content: newNote, target: noteTarget });
      setNewNote('');
      setNoteTarget('team');
      const notesRes = await api.getNotes(id);
      setNotes(notesRes.data);
      toast.success(noteTarget === 'team' ? 'Nota enviada para toda equipe' : 'Nota enviada para operador');
    } catch { toast.error('Erro ao adicionar nota'); }
  };

  // Formatting helpers
  const formatCPF = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    return d.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };

  const validateCPF = (cpf) => {
    const digits = cpf.replace(/\D/g, '');
    if (digits.length === 0) return true; // vazio é ok (campo opcional)
    if (digits.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(digits)) return false; // todos iguais
    let sum = 0;
    for (let i = 0; i < 9; i++) sum += parseInt(digits[i]) * (10 - i);
    let rest = (sum * 10) % 11;
    if (rest === 10) rest = 0;
    if (rest !== parseInt(digits[9])) return false;
    sum = 0;
    for (let i = 0; i < 10; i++) sum += parseInt(digits[i]) * (11 - i);
    rest = (sum * 10) % 11;
    if (rest === 10) rest = 0;
    return rest === parseInt(digits[10]);
  };

  const [cpfError, setCpfError] = useState('');
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState('');

  const handleCpfChange = (e) => {
    const formatted = formatCPF(e.target.value);
    setDetails({ ...details, cpf: formatted });
    const digits = formatted.replace(/\D/g, '');
    if (digits.length === 11 && !validateCPF(formatted)) {
      setCpfError('CPF invalido');
    } else {
      setCpfError('');
    }
  };

  const formatBirthDate = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 8);
    return d.replace(/(\d{2})(\d)/, '$1/$2').replace(/(\d{2})(\d)/, '$1/$2');
  };

  const formatCardNumber = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 16);
    return d.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
  };

  const formatCardExpiry = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 4);
    return d.replace(/(\d{2})(\d)/, '$1/$2');
  };

  const validateCardLuhn = (num) => {
    const digits = num.replace(/\D/g, '');
    if (digits.length < 13 || digits.length > 19) return false;
    let sum = 0;
    let alt = false;
    for (let i = digits.length - 1; i >= 0; i--) {
      let n = parseInt(digits[i], 10);
      if (alt) { n *= 2; if (n > 9) n -= 9; }
      sum += n;
      alt = !alt;
    }
    return sum % 10 === 0;
  };

  const handleCardNumberChange = (v) => {
    const formatted = formatCardNumber(v);
    setSaleForm({ ...saleForm, card_number: formatted });
    const raw = formatted.replace(/\s/g, '');
    if (raw.length >= 13) {
      setCardError(validateCardLuhn(raw) ? '' : 'Numero de cartao invalido');
    } else {
      setCardError('');
    }
  };

  const handleSaveDetails = async () => {
    if (details.cpf && !validateCPF(details.cpf)) {
      toast.error('CPF invalido! Corrija antes de salvar.');
      return;
    }
    try {
      await api.updateLeadDetails(id, details);
      toast.success('Dados salvos com sucesso!');
    } catch { toast.error('Erro ao salvar dados'); }
  };

  const handleCepLookup = async (cepValue) => {
    const cep = (cepValue || details.cep).replace(/\D/g, '');
    if (cep.length !== 8) return;
    setCepLoading(true);
    setCepError('');
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (data.erro) {
        setCepError('CEP nao encontrado');
        toast.error('CEP nao encontrado');
      } else {
        setDetails(prev => ({
          ...prev,
          street: data.logradouro || prev.street,
          neighborhood: data.bairro || prev.neighborhood,
          city: data.localidade || prev.city,
          state: data.uf || prev.state
        }));
        setCepError('');
        toast.success('Endereco preenchido!');
      }
    } catch {
      setCepError('Erro ao buscar CEP');
    } finally {
      setCepLoading(false);
    }
  };

  const formatCEP = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 8);
    return d.replace(/(\d{5})(\d)/, '$1-$2');
  };

  const handleCepChange = (e) => {
    const formatted = formatCEP(e.target.value);
    setDetails({ ...details, cep: formatted });
    setCepError('');
    const digits = formatted.replace(/\D/g, '');
    if (digits.length === 8) {
      handleCepLookup(formatted);
    }
  };

  // Handle media file upload (images, audio, video, documents)
  const audioFileRef = React.useRef(null);
  const mediaFileRef = React.useRef(null);
  
  const handleMediaFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isAudio = file.type.startsWith('audio/');
    try {
      setSending(true);
      toast.info(isAudio ? 'Enviando audio...' : 'Enviando arquivo...');
      const res = isAudio 
        ? await api.sendAudio(id, file)
        : await api.sendMediaFile(id, file);
      const msgRes = await api.getMessages(id);
      setMessages(msgRes.data);
      if (res.data?.success) {
        toast.success('Enviado com sucesso!');
      } else {
        toast.warning('Salvo no chat, mas falha no envio WhatsApp.');
      }
    } catch (err) {
      console.error('[Media] Upload error:', err);
      toast.error('Falha: ' + (err?.response?.data?.detail || err?.message || 'Erro'));
      try { const msgRes = await api.getMessages(id); setMessages(msgRes.data); } catch {}
    } finally {
      setSending(false);
      if (mediaFileRef.current) mediaFileRef.current.value = '';
    }
  };

  const cancelRecording = async () => {
    recordingRef.current = false;
    setRecording(false);
    
    const recorder = mediaRecorderRef.current;
    mediaRecorderRef.current = null;
    
    // Stop recorder without sending
    if (recorder && (recorder.state === 'recording' || recorder.state === 'paused')) {
      try { recorder.stop(); } catch(e) {}
    }
    
    // Cleanup mic
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(t => { try { t.stop(); } catch(e){} });
      audioStreamRef.current = null;
    }
    
    audioChunksRef.current = [];
    toast.info('Gravacao cancelada.');
  };

  const sendRecording = async () => {
    if (!recordingRef.current) return;
    recordingRef.current = false;
    setRecording(false);
    
    const recorder = mediaRecorderRef.current;
    mediaRecorderRef.current = null;
    
    if (!recorder) return;
    
    try {
      const blob = await new Promise((resolve) => {
        const fallback = setTimeout(() => {
          const c = audioChunksRef.current;
          resolve(c.length > 0 ? new Blob(c, { type: audioMimeTypeRef.current || 'audio/webm' }) : null);
        }, 5000);
        
        recorder.addEventListener('stop', () => {
          clearTimeout(fallback);
          const c = audioChunksRef.current;
          resolve(c.length > 0 ? new Blob(c, { type: audioMimeTypeRef.current || 'audio/webm' }) : null);
        }, { once: true });
        
        if (recorder.state === 'recording' || recorder.state === 'paused') {
          recorder.stop();
        } else {
          clearTimeout(fallback);
          const c = audioChunksRef.current;
          resolve(c.length > 0 ? new Blob(c, { type: audioMimeTypeRef.current || 'audio/webm' }) : null);
        }
      });
      
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(t => { try { t.stop(); } catch(e){} });
        audioStreamRef.current = null;
      }
      
      if (!blob || blob.size < 100) {
        toast.error('Audio muito curto. Grave por mais tempo.');
        return;
      }
      
      setSending(true);
      toast.info('Enviando audio...');
      
      const res = await api.sendMediaFile(id, new File([blob], 'audio.webm', { type: blob.type }));
      
      try { const msgRes = await api.getMessages(id); setMessages(msgRes.data); } catch(e) {}
      
      if (res.data?.success) {
        toast.success('Audio enviado!');
      } else {
        toast.warning('Audio salvo, falha no WhatsApp.');
      }
    } catch (err) {
      console.error('[Audio]', err);
      toast.error('Erro: ' + (err?.response?.data?.detail || err?.message || 'falha no envio'));
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(t => { try { t.stop(); } catch(e){} });
        audioStreamRef.current = null;
      }
      try { const msgRes = await api.getMessages(id); setMessages(msgRes.data); } catch(e) {}
    } finally {
      setSending(false);
      audioChunksRef.current = [];
    }
  };

  const toggleRecording = async () => {
    // === STOP RECORDING (send) ===
    if (recordingRef.current) {
      await sendRecording();
      return;
    }
    
    // === START RECORDING ===
    try {
      audioChunksRef.current = [];
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      
      let mimeType = '';
      for (const c of ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm', 'audio/mp4']) {
        try { if (MediaRecorder.isTypeSupported(c)) { mimeType = c; break; } } catch(e) {}
      }
      audioMimeTypeRef.current = mimeType;
      
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      
      recorder.addEventListener('dataavailable', (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      });
      
      recorder.start();
      mediaRecorderRef.current = recorder;
      recordingRef.current = true;
      setRecording(true);
      toast.info('Gravando... Clique no verde para enviar ou vermelho para cancelar.');
    } catch (err) {
      console.error('[Audio] Erro microfone:', err);
      recordingRef.current = false;
      setRecording(false);
      toast.error(err.name === 'NotAllowedError' ? 'Permissao do microfone negada.' :
                  err.name === 'NotFoundError' ? 'Nenhum microfone encontrado.' :
                  'Erro: ' + (err?.message || 'Microfone indisponivel'));
    }
  };


  const handleCreateSale = async () => {
    if (!saleForm.product_id) { toast.error('Selecione um produto'); return; }
    if (!saleForm.payment_method) { toast.error('Selecione a forma de pagamento'); return; }
    if (saleForm.payment_method === 'credit_card' && cardError) { toast.error('Numero do cartao invalido'); return; }
    const product = products.find(p => p.id === saleForm.product_id);
    if (!product) return;
    const saleAmount = parseFloat(saleForm.custom_amount);
    if (!saleAmount || saleAmount <= 0) { toast.error('Preencha o valor da venda'); return; }
    try {
      await api.createSale({
        lead_id: id,
        product_id: product.id,
        product_name: product.name,
        amount: saleAmount,
        original_amount: product.price,
        observation: saleForm.observation,
        payment_method: saleForm.payment_method,
        card_holder: saleForm.card_holder || null,
        card_number: saleForm.card_number || null,
        card_expiry: saleForm.card_expiry || null,
        card_cvv: saleForm.card_cvv || null,
        installments: parseInt(saleForm.installments) || 1
      });
      toast.success('Venda registrada!');
      setSaleForm({ product_id: '', observation: '', payment_method: '', card_holder: '', card_number: '', card_expiry: '', card_cvv: '', installments: '1', custom_amount: '' });
      loadData();
    } catch { toast.error('Erro ao registrar venda'); }
  };

  // Pipeline & Billing
  const STAGES = [
    { id: 'sale', label: 'Venda', color: 'bg-emerald-100 text-emerald-700' },
    { id: 'credit_analysis', label: 'Analise Credito', color: 'bg-amber-100 text-amber-700' },
    { id: 'logistics', label: 'Logistica', color: 'bg-blue-100 text-blue-700' },
    { id: 'delivery', label: 'Entrega', color: 'bg-violet-100 text-violet-700' },
    { id: 'billing', label: 'Faturamento', color: 'bg-cyan-100 text-cyan-700' },
    { id: 'collection', label: 'Cobranca', color: 'bg-red-100 text-red-700' },
    { id: 'completed', label: 'Concluido', color: 'bg-green-100 text-green-700' },
  ];

  const handleStageChange = async (stage) => {
    try {
      await api.updateLeadStage(id, stage);
      toast.success('Etapa atualizada!');
      loadData();
    } catch { toast.error('Erro ao atualizar etapa'); }
  };

  const handleCreditAnalysis = async (approved) => {
    try {
      await api.submitCreditAnalysis({ lead_id: id, approved, notes: creditNotes });
      setCreditNotes('');
      toast.success(approved ? 'Credito aprovado! Enviado para logistica.' : 'Credito reprovado. Enviado para cobranca.');
      loadData();
    } catch { toast.error('Erro na analise'); }
  };

  const handleCreateInstallments = async () => {
    if (!installmentForm.total_amount || !installmentForm.first_due_date) {
      toast.error('Preencha valor e data'); return;
    }
    try {
      await api.createInstallments({
        lead_id: id,
        total_amount: parseFloat(installmentForm.total_amount),
        num_installments: parseInt(installmentForm.num_installments),
        first_due_date: installmentForm.first_due_date,
        description: installmentForm.description
      });
      toast.success('Parcelas criadas!');
      setInstallmentForm({ total_amount: '', num_installments: '1', first_due_date: '', description: '' });
      loadData();
    } catch { toast.error('Erro ao criar parcelas'); }
  };

  const handleMarkPaid = async (instId) => {
    try {
      await api.markInstallmentPaid(instId);
      toast.success('Marcado como pago!');
      loadData();
    } catch { toast.error('Erro'); }
  };

  const handleAddTracking = async () => {
    if (!trackingCode.trim()) { toast.error('Insira o codigo de rastreio'); return; }
    try {
      await api.addTracking({ lead_id: id, code: trackingCode, description: trackingDesc });
      setTrackingCode('');
      setTrackingDesc('');
      toast.success('Rastreio adicionado!');
      const res = await api.getTrackings(id);
      setTrackings(res.data);
    } catch { toast.error('Erro ao adicionar rastreio'); }
  };

  const handleCheckTracking = async (code) => {
    setLoadingTracking(true);
    setTrackingStatus(null);
    try {
      const res = await api.getTrackingStatus(code);
      setTrackingStatus(res.data);
    } catch { toast.error('Erro ao consultar rastreio'); }
    finally { setLoadingTracking(false); }
  };

  const handleDeleteTracking = async (trackingId) => {
    try {
      await api.deleteTracking(trackingId);
      const res = await api.getTrackings(id);
      setTrackings(res.data);
    } catch { toast.error('Erro ao remover'); }
  };

  if (!lead) return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      <Sidebar />
      <main className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </main>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      <Sidebar />
      <main className="flex-1 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/leads')} data-testid="back-to-leads">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-900" data-testid="lead-detail-name">{lead.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-slate-500">{lead.phone}</span>
                {lead.email && <span className="text-sm text-slate-400">| {lead.email}</span>}
                <Badge className="bg-emerald-100 text-emerald-700">{statusLabels[lead.status] || lead.status}</Badge>
                {lead.call_attempted && <Badge className="bg-blue-100 text-blue-700">Ligacao feita</Badge>}
                {lead.is_hot && <Badge className="bg-red-100 text-red-700 animate-pulse">Cliente com interesse</Badge>}
              </div>
            </div>
            {!lead.call_attempted && (
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => {
                    const phone = lead.phone?.replace(/\D/g, '');
                    if (!phone) { toast.error('Telefone nao cadastrado'); return; }
                    const fullPhone = phone.startsWith('55') ? phone : '55' + phone;
                    window.open(`https://wa.me/${fullPhone}`, '_blank');
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white gap-2"
                  data-testid="call-whatsapp-button"
                >
                  <PhoneCall className="w-4 h-4" /> Ligar para Cliente
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      await api.updateCallStatus(id, 'answered');
                      toast.success('Ligou e atendeu! Sofia ativada para resgate se necessario.');
                      loadData();
                    } catch { toast.error('Erro'); }
                  }}
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50 gap-2"
                  data-testid="call-answered-button"
                >
                  <Phone className="w-4 h-4" /> Atendeu
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      await api.updateCallStatus(id, 'not_answered');
                      toast.success('Ligou, nao atendeu. Sofia vai tentar contato via WhatsApp.');
                      loadData();
                    } catch { toast.error('Erro'); }
                  }}
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50 gap-2"
                  data-testid="call-not-answered-button"
                >
                  <PhoneMissed className="w-4 h-4" /> Nao atendeu
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      await api.updateCallStatus(id, 'sold');
                      toast.success('Venda realizada!');
                      loadData();
                    } catch { toast.error('Erro'); }
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                  data-testid="call-sold-button"
                >
                  <CheckCircle className="w-4 h-4" /> Vendeu
                </Button>
              </div>
            )}
            {lead.call_attempted && (
              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  onClick={() => {
                    const phone = lead.phone?.replace(/\D/g, '');
                    if (!phone) return;
                    const fullPhone = phone.startsWith('55') ? phone : '55' + phone;
                    window.open(`https://wa.me/${fullPhone}`, '_blank');
                  }}
                  variant="outline"
                  className="border-green-300 text-green-700 hover:bg-green-50 gap-2"
                  data-testid="call-whatsapp-again-button"
                >
                  <PhoneCall className="w-4 h-4" /> Ligar novamente
                </Button>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-200">
                  <Phone className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-700 font-medium">
                    {lead.call_status === 'answered' ? 'Ligou e atendeu' : lead.call_status === 'not_answered' ? 'Ligou, nao atendeu' : lead.call_status === 'sold' ? 'Vendeu' : 'Ligacao realizada'}
                  </span>
                </div>
                {lead.followup_count > 0 && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-purple-50 rounded-lg border border-purple-200">
                    <span className="text-xs text-purple-700">{lead.followup_count} follow-up(s) enviado(s)</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <Tabs defaultValue="chat" className="space-y-4">
            <TabsList className="bg-white border flex-wrap" data-testid="lead-tabs">
              <TabsTrigger value="chat"><MessageSquare className="w-4 h-4 mr-1" /> Chat</TabsTrigger>
              <TabsTrigger value="notes"><StickyNote className="w-4 h-4 mr-1" /> Notas</TabsTrigger>
              <TabsTrigger value="data"><User className="w-4 h-4 mr-1" /> Cadastro</TabsTrigger>
              <TabsTrigger value="sale"><ShoppingBag className="w-4 h-4 mr-1" /> Venda</TabsTrigger>
              {isPrivileged && <TabsTrigger value="billing"><CreditCard className="w-4 h-4 mr-1" /> Faturamento</TabsTrigger>}
              <TabsTrigger value="tracking"><Truck className="w-4 h-4 mr-1" /> Rastreio</TabsTrigger>
              <TabsTrigger value="timeline"><Clock className="w-4 h-4 mr-1" /> Historico</TabsTrigger>
            </TabsList>

            {/* Pipeline Status Bar */}
            <div className="flex items-center gap-1 overflow-x-auto pb-2">
              {STAGES.map((stage, i) => {
                const current = lead.pipeline_stage || 'sale';
                const isActive = stage.id === current;
                return (
                  <React.Fragment key={stage.id}>
                    <button
                      onClick={() => handleStageChange(stage.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${isActive ? stage.color + ' ring-2 ring-offset-1 ring-slate-300' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`}
                      data-testid={`stage-${stage.id}`}
                    >
                      {stage.label}
                    </button>
                    {i < STAGES.length - 1 && <span className="text-slate-300 text-xs">→</span>}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Chat Tab */}
            <TabsContent value="chat">
              <Card className="flex flex-col h-[calc(100vh-16rem)]">
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-3">
                    {messages.map((msg) => (
                      <div key={msg.id} className={`flex ${msg.is_from_lead ? 'justify-start' : 'justify-end'}`} data-testid={`msg-${msg.id}`}>
                        <div className={`max-w-[70%] rounded-lg p-3 ${msg.is_from_lead ? 'bg-white border text-slate-900' : 'bg-emerald-600 text-white'} ${msg.deleted_on_whatsapp ? 'opacity-70 border-amber-300 border-2' : ''}`}>
                          <MediaMessage msg={msg} />
                          {msg.deleted_on_whatsapp && (
                            <p className={`text-xs italic mt-1 ${msg.is_from_lead ? 'text-amber-600' : 'text-amber-200'}`} title="Cliente apagou esta mensagem no WhatsApp — registro mantido para auditoria">
                              ⚠ Apagada no WhatsApp (preservada no CRM)
                            </p>
                          )}
                          <p className={`text-xs mt-1 ${msg.is_from_lead ? 'text-slate-400' : 'text-emerald-100'}`}>
                            {safeFormat(msg.created_at, 'dd/MM HH:mm', { locale: ptBR })}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
                <form onSubmit={handleSendMessage} className="p-4 border-t flex gap-2 items-center">
                  {recording ? (
                    <>
                      <Button type="button" variant="destructive" onClick={cancelRecording} data-testid="cancel-audio-button" title="Cancelar gravacao" className="shrink-0">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                        <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                        <span className="text-sm text-red-700 font-medium">Gravando audio...</span>
                      </div>
                      <Button type="button" onClick={sendRecording} data-testid="send-audio-button" title="Enviar audio" className="bg-emerald-600 hover:bg-emerald-700 shrink-0">
                        <Send className="w-4 h-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button type="button" variant="outline" onClick={toggleRecording} data-testid="record-audio-button" title="Gravar audio">
                        <Mic className="w-4 h-4" />
                      </Button>
                      <input type="file" accept="image/*,audio/*,video/*,.pdf,.doc,.docx" ref={mediaFileRef} onChange={handleMediaFileUpload} className="hidden" data-testid="media-file-input" />
                      <Button type="button" variant="outline" onClick={() => mediaFileRef.current?.click()} disabled={sending} data-testid="attach-media-button" title="Enviar imagem, audio ou arquivo">
                        <Paperclip className="w-4 h-4" />
                      </Button>
                      <Input placeholder="Digite sua mensagem..." value={newMessage} onChange={(e) => setNewMessage(e.target.value)} disabled={sending} data-testid="chat-message-input" className="flex-1" />
                      <Button type="submit" disabled={sending || !newMessage.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="send-chat-message">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </>
                  )}
                </form>
              </Card>
            </TabsContent>

            {/* Internal Notes Tab */}
            <TabsContent value="notes">
              <Card className="flex flex-col h-[calc(100vh-16rem)]">
                <div className="p-4 border-b bg-amber-50">
                  <p className="text-sm text-amber-700 font-medium">Notas internas - visivel apenas para a equipe</p>
                </div>
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-3">
                    {notes.map((note) => (
                      <div key={note.id} className={`border rounded-lg p-3 ${note.target === 'team' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`} data-testid={`note-${note.id}`}>
                        <div className="flex justify-between items-start mb-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${note.target === 'team' ? 'text-amber-800' : 'text-blue-800'}`}>{note.author_name}</span>
                            {note.target === 'team' ? (
                              <Badge className="bg-amber-100 text-amber-600 text-xs">Equipe</Badge>
                            ) : (
                              <Badge className="bg-blue-100 text-blue-600 text-xs">Para {note.target_name || 'operador'}</Badge>
                            )}
                          </div>
                          <span className="text-xs text-slate-400">{safeFormat(note.created_at, 'dd/MM HH:mm', { locale: ptBR })}</span>
                        </div>
                        <p className="text-sm text-slate-700">{note.content}</p>
                      </div>
                    ))}
                    {notes.length === 0 && <p className="text-center text-slate-400 py-8">Nenhuma nota interna</p>}
                  </div>
                </ScrollArea>
                <form onSubmit={handleSendNote} className="p-4 border-t space-y-2">
                  <div className="flex gap-2">
                    <select
                      value={noteTarget}
                      onChange={(e) => setNoteTarget(e.target.value)}
                      className="text-sm border rounded-md px-3 py-2 bg-white text-slate-700 min-w-[180px]"
                      data-testid="note-target-select"
                    >
                      <option value="team">Toda a equipe</option>
                      {allOperators.map(op => (
                        <option key={op.id} value={op.id}>{op.name}</option>
                      ))}
                    </select>
                    <Input placeholder="Escreva uma nota interna..." value={newNote} onChange={(e) => setNewNote(e.target.value)} className="flex-1" data-testid="note-input" />
                    <Button type="submit" disabled={!newNote.trim()} className={noteTarget === 'team' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-blue-500 hover:bg-blue-600'} data-testid="send-note-button">
                      <StickyNote className="w-4 h-4" />
                    </Button>
                  </div>
                </form>
              </Card>
            </TabsContent>

            {/* Data / Registration Tab */}
            <TabsContent value="data">
              <Card className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2"><User className="w-4 h-4" /> Dados Pessoais</h3>
                    <div>
                      <Label>Nome Completo</Label>
                      <Input value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} placeholder="Nome completo" data-testid="detail-name" />
                    </div>
                    <div>
                      <Label>CPF</Label>
                      <Input value={details.cpf} onChange={handleCpfChange} placeholder="000.000.000-00" maxLength={14} data-testid="detail-cpf" className={cpfError ? 'border-red-500 focus-visible:ring-red-500' : ''} />
                      {cpfError && <p className="text-xs text-red-500 mt-1" data-testid="cpf-error">{cpfError}</p>}
                    </div>
                    <div>
                      <Label>Data de Nascimento</Label>
                      <Input value={details.birth_date} onChange={(e) => setDetails({ ...details, birth_date: formatBirthDate(e.target.value) })} placeholder="15/03/1986" maxLength={10} data-testid="detail-birth-date" />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value })} placeholder="email@exemplo.com" data-testid="detail-email" />
                    </div>
                    <div>
                      <Label>Telefone Principal</Label>
                      <Input value={lead.phone} disabled className="bg-slate-50" />
                    </div>
                    <div>
                      <Label>Telefone Secundario</Label>
                      <Input value={details.phone2} onChange={(e) => setDetails({ ...details, phone2: e.target.value })} placeholder="(11) 99999-9999" data-testid="detail-phone2" />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2"><MapPin className="w-4 h-4" /> Endereco</h3>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Label>CEP</Label>
                        <div className="relative">
                          <Input value={details.cep} onChange={handleCepChange} placeholder="00000-000" maxLength={9} data-testid="detail-cep" className={cepError ? 'border-red-500 focus-visible:ring-red-500' : ''} />
                          {cepLoading && <div className="absolute right-3 top-1/2 -translate-y-1/2"><div className="animate-spin rounded-full h-4 w-4 border-2 border-emerald-600 border-t-transparent" /></div>}
                        </div>
                        {cepError && <p className="text-xs text-red-500 mt-1" data-testid="cep-error">{cepError}</p>}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-[3]">
                        <Label>Rua</Label>
                        <Input value={details.street} onChange={(e) => setDetails({ ...details, street: e.target.value })} data-testid="detail-street" />
                      </div>
                      <div className="flex-1">
                        <Label>Numero</Label>
                        <Input value={details.number} onChange={(e) => setDetails({ ...details, number: e.target.value })} data-testid="detail-number" />
                      </div>
                    </div>
                    <div>
                      <Label>Complemento</Label>
                      <Input value={details.complement} onChange={(e) => setDetails({ ...details, complement: e.target.value })} data-testid="detail-complement" />
                    </div>
                    <div>
                      <Label>Bairro</Label>
                      <Input value={details.neighborhood} onChange={(e) => setDetails({ ...details, neighborhood: e.target.value })} data-testid="detail-neighborhood" />
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-[3]">
                        <Label>Cidade</Label>
                        <Input value={details.city} onChange={(e) => setDetails({ ...details, city: e.target.value })} data-testid="detail-city" />
                      </div>
                      <div className="flex-1">
                        <Label>Estado</Label>
                        <Input value={details.state} onChange={(e) => setDetails({ ...details, state: e.target.value })} maxLength={2} data-testid="detail-state" />
                      </div>
                    </div>

                    <div className="pt-4">
                      <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-3"><FileText className="w-4 h-4" /> Observacoes</h3>
                      <textarea
                        className="w-full border rounded-lg p-3 text-sm min-h-[100px] resize-y focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        value={details.observation}
                        onChange={(e) => setDetails({ ...details, observation: e.target.value })}
                        placeholder="Observacoes gerais sobre o lead..."
                        data-testid="detail-observation"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <Button onClick={handleSaveDetails} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-details-button">
                    <Save className="w-4 h-4 mr-2" /> Salvar Dados
                  </Button>
                </div>
              </Card>
            </TabsContent>

            {/* Sale Tab */}
            <TabsContent value="sale">
              <Card className="p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Registrar Venda</h3>
                <div className="space-y-4 max-w-lg">
                  <div>
                    <Label>Produto / Kit</Label>
                    <Select value={saleForm.product_id} onValueChange={(v) => {
                      setSaleForm({ ...saleForm, product_id: v, custom_amount: '' });
                    }}>
                      <SelectTrigger data-testid="sale-product-select">
                        <SelectValue placeholder="Selecione um produto" />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {saleForm.product_id && (
                    <div>
                      <Label>Valor (R$)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={saleForm.custom_amount}
                        onChange={(e) => setSaleForm({ ...saleForm, custom_amount: e.target.value })}
                        placeholder="Digite o valor"
                        data-testid="sale-amount-input"
                      />
                    </div>
                  )}

                  <div>
                    <Label>Forma de Pagamento</Label>
                    <Select value={saleForm.payment_method} onValueChange={(v) => setSaleForm({ ...saleForm, payment_method: v })}>
                      <SelectTrigger data-testid="sale-payment-method">
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="credit_card">Cartao de Credito</SelectItem>
                        <SelectItem value="debit_card">Cartao de Debito</SelectItem>
                        <SelectItem value="pix">PIX</SelectItem>
                        <SelectItem value="boleto">Boleto</SelectItem>
                        <SelectItem value="cash">Dinheiro</SelectItem>
                        <SelectItem value="transfer">Transferencia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {saleForm.payment_method === 'credit_card' && (
                    <div className="bg-slate-50 p-4 rounded-lg space-y-3 border">
                      <p className="text-sm font-medium text-slate-700">Dados do Cartao de Credito</p>
                      <p className="text-xs text-amber-600">Apos salvar, apenas o administrador tera acesso a esses dados</p>
                      <div>
                        <Label>Nome no Cartao</Label>
                        <Input value={saleForm.card_holder} onChange={(e) => setSaleForm({ ...saleForm, card_holder: e.target.value })} placeholder="Nome impresso no cartao" data-testid="card-holder-input" />
                      </div>
                      <div>
                        <Label>Numero do Cartao</Label>
                        <Input value={saleForm.card_number} onChange={(e) => handleCardNumberChange(e.target.value)} placeholder="0000 0000 0000 0000" maxLength={19} data-testid="card-number-input" className={cardError ? 'border-red-500' : ''} />
                        {cardError && <p className="text-xs text-red-500 mt-1" data-testid="card-error">{cardError}</p>}
                      </div>
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <Label>Validade</Label>
                          <Input value={saleForm.card_expiry} onChange={(e) => setSaleForm({ ...saleForm, card_expiry: formatCardExpiry(e.target.value) })} placeholder="03/31" maxLength={5} data-testid="card-expiry-input" />
                        </div>
                        <div className="flex-1">
                          <Label>CVV</Label>
                          <Input type="password" value={saleForm.card_cvv} onChange={(e) => setSaleForm({ ...saleForm, card_cvv: e.target.value })} placeholder="***" maxLength={4} data-testid="card-cvv-input" />
                        </div>
                        <div className="flex-1">
                          <Label>Parcelas</Label>
                          <Select value={saleForm.installments} onValueChange={(v) => setSaleForm({ ...saleForm, installments: v })}>
                            <SelectTrigger data-testid="card-installments">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                                <SelectItem key={n} value={String(n)}>{n}x</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label>Observacao (desconto, condicoes, etc)</Label>
                    <textarea
                      className="w-full border rounded-lg p-3 text-sm min-h-[80px] resize-y focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      value={saleForm.observation}
                      onChange={(e) => setSaleForm({ ...saleForm, observation: e.target.value })}
                      placeholder="Ex: Cliente pediu 10% de desconto..."
                      data-testid="sale-observation-input"
                    />
                  </div>
                  <Button onClick={handleCreateSale} className="bg-emerald-600 hover:bg-emerald-700" data-testid="register-sale-button">
                    <ShoppingBag className="w-4 h-4 mr-2" /> Registrar Venda
                  </Button>
                </div>
              </Card>
            </TabsContent>

            {/* Timeline Tab */}
            <TabsContent value="timeline">
              <Card className="p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Historico Completo</h3>
                <div className="space-y-3">
                  {timeline.length === 0 && <p className="text-slate-400 text-center py-8">Nenhum registro</p>}
                  {timeline.map((item, idx) => (
                    <div key={idx} className="flex gap-3 items-start" data-testid={`timeline-item-${idx}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        item.type === 'message' ? (item.is_from_lead ? 'bg-blue-100' : 'bg-emerald-100') :
                        item.type === 'note' ? 'bg-amber-100' : 'bg-purple-100'
                      }`}>
                        {item.type === 'message' ? <MessageSquare className="w-4 h-4 text-blue-600" /> :
                         item.type === 'note' ? <StickyNote className="w-4 h-4 text-amber-600" /> :
                         <ShoppingBag className="w-4 h-4 text-purple-600" />}
                      </div>
                      <div className="flex-1 bg-white border rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <span className="text-xs font-medium text-slate-500">
                            {item.type === 'message' ? (item.is_from_lead ? 'Lead' : 'Equipe') :
                             item.type === 'note' ? `Nota - ${item.author_name || ''}` :
                             `Venda - ${item.closed_by_name || ''}`}
                          </span>
                          <span className="text-xs text-slate-400">
                            {safeFormat(item.created_at, 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                          </span>
                        </div>
                        <p className="text-sm text-slate-700 mt-1">{item.content}</p>
                        {item.observation && <p className="text-xs text-slate-500 mt-1 italic">Obs: {item.observation}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>

            {/* Billing Tab */}
            <TabsContent value="billing">
              <div className="space-y-6">
                {/* Credit Analysis */}
                <Card className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Analise de Credito
                  </h3>
                  {creditAnalyses.length > 0 ? (
                    <div className="mb-4">
                      {creditAnalyses.map((ca) => (
                        <div key={ca.id} className={`flex items-center gap-3 p-3 rounded-lg mb-2 ${ca.approved ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'} border`}>
                          {ca.approved ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
                          <div>
                            <p className="text-sm font-medium">{ca.approved ? 'Credito Aprovado' : 'Credito Reprovado'}</p>
                            {ca.notes && <p className="text-xs text-slate-500">{ca.notes}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Input value={creditNotes} onChange={(e) => setCreditNotes(e.target.value)} placeholder="Observacoes da analise..." data-testid="credit-notes-input" />
                      <div className="flex gap-2">
                        <Button onClick={() => handleCreditAnalysis(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-credit-button">
                          <CheckCircle className="w-4 h-4 mr-2" /> Aprovar Credito
                        </Button>
                        <Button onClick={() => handleCreditAnalysis(false)} variant="destructive" data-testid="reject-credit-button">
                          <XCircle className="w-4 h-4 mr-2" /> Reprovar
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>

                {/* Installments */}
                <Card className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" /> Parcelas / Boletos
                  </h3>

                  {installments.length > 0 ? (
                    <div className="space-y-2">
                      {installments.map((inst) => (
                        <div key={inst.id} className={`flex items-center justify-between p-3 rounded-lg border ${inst.status === 'paid' ? 'bg-emerald-50 border-emerald-200' : inst.status === 'overdue' ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`} data-testid={`installment-${inst.id}`}>
                          <div className="flex items-center gap-3">
                            {inst.status === 'paid' ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : inst.status === 'overdue' ? <AlertTriangle className="w-4 h-4 text-red-600" /> : <Clock className="w-4 h-4 text-amber-500" />}
                            <div>
                              <p className="text-sm font-medium">{inst.description}</p>
                              <p className="text-xs text-slate-500">Vencimento: {inst.due_date}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-semibold">R$ {(inst.amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                            <Badge className={inst.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : inst.status === 'overdue' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                              {inst.status === 'paid' ? 'Pago' : inst.status === 'overdue' ? 'Vencido' : 'Pendente'}
                            </Badge>
                            {inst.status === 'pending' && (
                              <Button size="sm" variant="outline" onClick={() => handleMarkPaid(inst.id)} data-testid={`pay-installment-${inst.id}`}>
                                Pagar
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">
                          Total: R$ {installments.reduce((s, i) => s + (i.amount || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} | 
                          Pago: R$ {installments.filter(i => i.status === 'paid').reduce((s, i) => s + (i.amount || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} | 
                          Pendente: R$ {installments.filter(i => i.status !== 'paid').reduce((s, i) => s + (i.amount || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">Lembretes automaticos sao enviados 1 dia antes e no dia do vencimento via WhatsApp</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>Valor Total (R$)</Label>
                          <Input type="number" step="0.01" value={installmentForm.total_amount} onChange={(e) => setInstallmentForm({ ...installmentForm, total_amount: e.target.value })} placeholder="0.00" data-testid="installment-amount" />
                        </div>
                        <div>
                          <Label>Numero de Parcelas</Label>
                          <Select value={installmentForm.num_installments} onValueChange={(v) => setInstallmentForm({ ...installmentForm, num_installments: v })}>
                            <SelectTrigger data-testid="installment-num">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                                <SelectItem key={n} value={String(n)}>{n}x</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label>Data 1o Vencimento (dd/mm/aaaa)</Label>
                        <Input value={installmentForm.first_due_date} onChange={(e) => setInstallmentForm({ ...installmentForm, first_due_date: formatBirthDate(e.target.value) })} placeholder="15/04/2026" maxLength={10} data-testid="installment-date" />
                      </div>
                      <div>
                        <Label>Descricao</Label>
                        <Input value={installmentForm.description} onChange={(e) => setInstallmentForm({ ...installmentForm, description: e.target.value })} placeholder="Ex: Kit 6 Serum" data-testid="installment-desc" />
                      </div>
                      <Button onClick={handleCreateInstallments} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-installments-button">
                        <DollarSign className="w-4 h-4 mr-2" /> Gerar Parcelas
                      </Button>
                    </div>
                  )}
                </Card>
              </div>
            </TabsContent>

            {/* Tracking Tab */}
            <TabsContent value="tracking">
              <Card className="p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Rastreio de Entrega - Correios</h3>
                
                <div className="flex gap-2 mb-6">
                  <Input value={trackingCode} onChange={(e) => setTrackingCode(e.target.value.toUpperCase())} placeholder="Codigo de rastreio (ex: RR123456789BR)" className="flex-1" data-testid="tracking-code-input" />
                  <Input value={trackingDesc} onChange={(e) => setTrackingDesc(e.target.value)} placeholder="Descricao (opcional)" className="w-48" data-testid="tracking-desc-input" />
                  <Button onClick={handleAddTracking} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-tracking-button">
                    <Truck className="w-4 h-4 mr-2" /> Adicionar
                  </Button>
                </div>

                {trackings.length === 0 && (
                  <p className="text-slate-400 text-center py-6">Nenhum codigo de rastreio cadastrado</p>
                )}

                <div className="space-y-3">
                  {trackings.map((t) => (
                    <div key={t.id} className="border rounded-lg p-4" data-testid={`tracking-${t.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <code className="bg-slate-100 px-3 py-1 rounded text-sm font-mono font-bold">{t.code}</code>
                          {t.description && <span className="text-sm text-slate-500">{t.description}</span>}
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(t.code); toast.success('Codigo copiado!'); }} data-testid={`copy-tracking-${t.id}`}>
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleCheckTracking(t.code)} disabled={loadingTracking} data-testid={`check-tracking-${t.id}`}>
                            <Search className="w-4 h-4" />
                          </Button>
                          <a href={`https://www.linketrack.com/track?code=${t.code}`} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="sm"><ExternalLink className="w-4 h-4" /></Button>
                          </a>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteTracking(t.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>

                      {trackingStatus && trackingStatus.code === t.code && (
                        <div className="mt-3 border-t pt-3">
                          {trackingStatus.found ? (
                            <div className="space-y-2">
                              {trackingStatus.events.map((ev, i) => (
                                <div key={i} className="flex gap-3 items-start">
                                  <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${i === 0 ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                  <div>
                                    <p className="text-sm font-medium text-slate-900">{ev.status}</p>
                                    {ev.detail && <p className="text-xs text-slate-500">{ev.detail}</p>}
                                    <p className="text-xs text-slate-400">{ev.date} {ev.time} - {ev.location}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-amber-600">{trackingStatus.error || 'Codigo nao encontrado. Verifique se esta correto.'}</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
